import React, { type PropsWithChildren, type ReactNode } from "react";
import FilterFieldset from "./filter-fieldset";
import DesignersFilterFieldset from "./designer-filter-fieldset";
import type { FilterOption, FilterOptions } from "components/types/filter-options";
import type { WorkingFilters } from "components/types/working-filters";
import { flagsAndExperimentNames, useOnLoadFlagOrExperiment } from "../hoc/with-flags-and-experiments";
import SelectedFilters from "./selected-filters";
import { isKeyOf } from "components/types/type-utils";

export type FacetFiltersProps = PropsWithChildren<{
  filterOptions: FilterOptions;
  filters: WorkingFilters;
  getFieldsetHeader?: (fieldset: string) => string | null;
  idSuffix: string;
  isFilterOpen: (filter: string) => boolean;
  isSwapsGrid?: boolean;
  onChange: (filter: { filterGroupKey: string; name: string; selected: boolean }) => void;
  onClearFilters: () => void;
  onToggleOpenFilters: (filterKey: string, isOpen: boolean) => void;
  renderRTRFilters: () => ReactNode;
}>;

const FacetFilters = ({
  children,
  filterOptions,
  filters,
  getFieldsetHeader = () => null,
  idSuffix,
  isFilterOpen,
  isSwapsGrid,
  onChange,
  onClearFilters,
  onToggleOpenFilters,
  renderRTRFilters,
}: FacetFiltersProps) => {
  const omittedFilterOptions = ["designer", "bodyTypes", "canonicalSizes", "eventType", "ageRanges", "partitionId"];
  const seasonDisplayNames = {
    fall_winter: "cold weather",
    spring_summer: "warm weather",
    seasonless: "year-round",
  };
  const className = "facet-filters";

  const filterOrder = [
    "epicCategories",
    "occasions",
    "epicUseCaseEvents",
    "epicStylePersonas",
    "epicFormality",
    "formality",
    "colors",
    "epicPrint",
    "season",
    "epicSeasonality",
    "length",
    "epicLength",
    "epicSleeve",
    "sleeve",
    "maternity",
    "epicMaternity",
    "epicEmbellishments",
    "embellishments",
    "epicNeckline",
    "neckline",
    "designer",
    "hearts",
    "canonicalSizes",
    "similar_to",
    "fit",
  ];

  const [reserveFilterFlag] = useOnLoadFlagOrExperiment(flagsAndExperimentNames.RESERVE_FILTER);

  const getEnabledFilters = () => {
    const enabledFilters = Object.keys(filterOptions).filter(filterKey => !omittedFilterOptions.includes(filterKey));

    return enabledFilters.sort((a, b) => filterOrder.indexOf(a) - filterOrder.indexOf(b));
  };

  const editSeasonCopy = (seasonFilterFromBackend: FilterOption) => {
    const editedSeasonFilter = { ...seasonFilterFromBackend };

    // Top-level request for seasonality copy to be: WEATHER
    // Warm weather appropriate (spring / summer, seasonless)
    // Cold weather appropriate (fall / winter, seasonless)
    // Year-Round - Seasonless
    editedSeasonFilter.displayName = "Weather";
    //Cloning the parent doesn't clone the child, because Javascript is fun, and props are immutable
    editedSeasonFilter.values = editedSeasonFilter.values.map(val => ({
      ...val,
      displayName: isKeyOf(seasonDisplayNames, val.id) ? seasonDisplayNames[val.id] : val.displayName,
    }));

    return editedSeasonFilter;
  };

  /**
   * Apply any client-side changes to the filters as returned from the BE.
   */
  const getModifiedFilters = (enabledFilters: string[]) => {
    const filterOptionsClone = { ...filterOptions };

    if (enabledFilters.includes("season") && filterOptionsClone.season) {
      filterOptionsClone.season = editSeasonCopy(filterOptionsClone.season);
    }

    return filterOptionsClone;
  };

  const renderFilters = () => {
    const enabledFilters = getEnabledFilters();
    const filterOptions = getModifiedFilters(enabledFilters);

    return enabledFilters.map(filterKey => {
      if (
        !filterOptions[filterKey] ||
        //Some non-attribute filters (i.e. availableOnly) aren't arrays
        //We wouldn't display them here anyway, but typeguard to appease Typescript
        (typeof filters[filterKey] !== "undefined" && !Array.isArray(filters[filterKey]))
      ) {
        return null;
      }

      const { displayName, values } = filterOptions[filterKey];

      return (
        <FilterFieldset
          key={filterKey + idSuffix}
          filterOpen={isFilterOpen(filterKey)}
          onToggleOpenFilters={onToggleOpenFilters}
          onChange={onChange}
          keyName={filterKey}
          filters={filters[filterKey]}
          fieldsetHeader={getFieldsetHeader(filterKey)}
          displayName={displayName}
          values={values}
        />
      );
    });
  };

  const renderSizeFilter = () => {
    //Swaps was historically the only grid showing a size selector here
    //It will be replaced with UX built as part of Reserve Filter work
    if (!isSwapsGrid || reserveFilterFlag) {
      return;
    }

    const fieldsetHeader = getFieldsetHeader("designer") ?? null;

    const { canonicalSizes } = filterOptions;

    if (canonicalSizes && Object.keys(canonicalSizes).length) {
      return (
        <FilterFieldset
          key={"canonicalSizes" + idSuffix}
          onToggleOpenFilters={onToggleOpenFilters}
          filterOpen={isFilterOpen("canonicalSizes")}
          displayName={"Size"}
          keyName={"canonicalSizes"}
          onChange={onChange}
          filters={filters.canonicalSizes ?? []}
          values={canonicalSizes.values}
          fieldsetHeader={fieldsetHeader}
        />
      );
    }
  };

  const renderDesignersFilter = () => {
    if (typeof filterOptions.designer === "undefined") {
      return null;
    }

    const fieldsetHeader = getFieldsetHeader("designer") ?? null;
    const { displayName, values } = filterOptions.designer;

    return (
      <DesignersFilterFieldset
        key={"designer" + idSuffix}
        filterOpen={isFilterOpen("designer")}
        onToggleOpenFilters={onToggleOpenFilters}
        onChange={onChange}
        filters={filters.designer}
        keyName="designer"
        fieldsetHeader={fieldsetHeader}
        displayName={displayName}
        values={values}
        idSuffix={idSuffix}
      />
    );
  };

  return (
    <div>
      <SelectedFilters onClearFilters={onClearFilters} onClearSingleFilter={onChange} />
      <div className={className}>
        {renderSizeFilter()}
        {renderRTRFilters()}
        {renderFilters()}
        {renderDesignersFilter()}
        {children}
      </div>
    </div>
  );
};

export default FacetFilters;
